import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Slider } from '../components/ui/slider';
import api from '../utils/api';
import { toast } from 'sonner';
import { ChevronRight, Loader2, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { DictationButton } from '../components/DictationButton';

export const CheckIn = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [reflection, setReflection] = useState('');

  const QUESTIONS = {
    core: [
      { key: 'physical_health', label: t('assessment.physicalHealth') },
      { key: 'strength_fitness', label: t('assessment.strengthFitness') },
      { key: 'energy', label: t('assessment.energy') },
      { key: 'environment_order', label: t('assessment.environmentOrder') },
      { key: 'work_mission', label: t('assessment.workMission') },
      { key: 'money_financial', label: t('assessment.moneyFinancial') },
      { key: 'love_relationship', label: t('assessment.loveRelationship') },
      { key: 'friends_community', label: t('assessment.friendsCommunity') },
      { key: 'emotional_stability', label: t('assessment.emotionalStability') },
      { key: 'discipline_consistency', label: t('assessment.disciplineConsistency') },
      { key: 'learning_growth', label: t('assessment.learningGrowth') },
      { key: 'meaning_purpose', label: t('assessment.meaningPurpose') },
    ],
  };
  
  const [scores, setScores] = useState({
    physical_health: 5,
    strength_fitness: 5,
    energy: 5,
    environment_order: 5,
    work_mission: 5,
    money_financial: 5,
    love_relationship: 5,
    friends_community: 5,
    emotional_stability: 5,
    discipline_consistency: 5,
    learning_growth: 5,
    meaning_purpose: 5,
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/assessment', {
        scores,
        meta_scores: {
          direction: 5,
          alignment: 5,
          momentum: 5,
          stress_overwhelm: 5,
        },
        written_answers: {
          building_achieving: reflection,
          biggest_problem: '',
          most_stuck: '',
          going_well: '',
          avoiding: '',
          one_year_different: '',
          anything_else: '',
        },
        assessment_type: 'checkin',
      });
      toast.success(t('checkin.success'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('checkin.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans mb-8 flex items-center gap-2"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('nav.backToDashboard')}
        </button>

        <div className="text-center mb-12">
          <h2 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
            {t('checkin.title')}
          </h2>
          <p className="font-sans font-light text-[#D1D1D6] text-lg">
            {t('checkin.subtitle')}
          </p>
        </div>

        <div className="space-y-8" data-testid="checkin-questions">
          {QUESTIONS.core.map((q, index) => (
            <motion.div
              key={q.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="glass-panel rounded-xl p-6"
              data-testid={`checkin-question-${q.key}`}
            >
              <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-3">
                {q.label}
              </p>
              <div className="flex items-center gap-4">
                <span className="text-[#A1A1AA] font-sans text-sm w-8">1</span>
                <Slider
                  value={[scores[q.key]]}
                  onValueChange={(value) => setScores({ ...scores, [q.key]: value[0] })}
                  min={1}
                  max={10}
                  step={1}
                  className="flex-1"
                  data-testid={`checkin-slider-${q.key}`}
                />
                <span className="text-[#A1A1AA] font-sans text-sm w-8">10</span>
                <span className="text-[#D4AF37] font-sans font-semibold text-xl w-12 text-right">
                  {scores[q.key]}
                </span>
              </div>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-panel rounded-xl p-6"
            data-testid="weekly-reflection"
          >
            <label className="font-sans font-light text-[#F8F5F2] text-lg block mb-3">
              {t('checkin.reflectionLabel')}
            </label>
            <div className="relative">
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                className="w-full bg-white/5 border border-[#D4AF37]/15 rounded-lg px-4 py-3 pr-12 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none transition-all min-h-[120px] resize-y"
                placeholder={t('checkin.reflectionPlaceholder')}
                data-testid="reflection-textarea"
              />
              <div className="absolute right-2 top-2">
                <DictationButton 
                  onTranscript={(text) => setReflection(prev => prev ? `${prev} ${text}` : text)}
                />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="flex justify-center pt-8">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.15)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] font-sans text-lg flex items-center gap-2 disabled:opacity-50"
            data-testid="submit-checkin-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('checkin.updating')}
              </>
            ) : (
              <>
                {t('checkin.complete')} <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckIn;
